import { z } from 'zod';
import { powertrainTypeSchema, publishedRegimeSchema, vehicleStatusSchema } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
export const MIN_MODEL_YEAR = 1980;
export const MAX_MODEL_YEAR = 2100;
export const MIN_TIRE_PRESSURE_KPA = 100;
export const MAX_TIRE_PRESSURE_KPA = 500;
/** OEM cold tire pressures, one per wheel — stored as a single jsonb column (plan §4). */
export const tirePressureSetSchema = z.object({
    fl: z.coerce.number().min(MIN_TIRE_PRESSURE_KPA).max(MAX_TIRE_PRESSURE_KPA),
    fr: z.coerce.number().min(MIN_TIRE_PRESSURE_KPA).max(MAX_TIRE_PRESSURE_KPA),
    rl: z.coerce.number().min(MIN_TIRE_PRESSURE_KPA).max(MAX_TIRE_PRESSURE_KPA),
    rr: z.coerce.number().min(MIN_TIRE_PRESSURE_KPA).max(MAX_TIRE_PRESSURE_KPA),
});
export const publishedVehicleResultSchema = z.object({
    id: uuidSchema,
    vehicleId: uuidSchema,
    regime: publishedRegimeSchema,
    metric: z.string().min(1),
    value: z.number(),
    unit: z.string().min(1),
    source: z.string().nullable(),
    sourceDate: z.string().nullable(),
    market: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Every spec field of a vehicle, shared by the list row and the detail payload. */
export const vehicleSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    segmentId: uuidSchema,
    segmentName: z.string().min(1),
    manufacturer: z.string().min(1),
    model: z.string().min(1),
    modelYear: z.number().int(),
    trim: z.string().nullable(),
    vin: z.string().nullable(),
    powertrainType: powertrainTypeSchema,
    engine: z.string().nullable(),
    powerHp: z.number().int().nullable(),
    torqueNm: z.number().int().nullable(),
    transmission: z.string().nullable(),
    drivetrain: z.string().nullable(),
    fuelType: z.string().nullable(),
    defaultFuelGrade: z.string().nullable(),
    tireSize: z.string().nullable(),
    oemTirePressureKpa: tirePressureSetSchema.nullable(),
    odometerKm: z.number().int().nullable(),
    notes: z.string().nullable(),
    status: vehicleStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const vehicleDetailSchema = vehicleSchema.extend({
    publishedResults: z.array(publishedVehicleResultSchema),
});
export const VEHICLE_SORT_FIELDS = ['code', 'manufacturer', 'modelYear', 'createdAt'];
export const vehicleSortBySchema = z.enum(VEHICLE_SORT_FIELDS);
export const listVehiclesInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: vehicleSortBySchema.default('code'),
    /** Matches code, manufacturer, model or VIN, case-insensitively. */
    search: z.string().trim().min(1).optional(),
    segmentId: uuidSchema.optional(),
    powertrainType: powertrainTypeSchema.optional(),
    manufacturer: z.string().trim().min(1).optional(),
    status: vehicleStatusSchema.optional(),
});
export const listVehiclesOutputSchema = paginatedSchema(vehicleSchema);
/** The distinct values behind the vehicle list's filter dropdowns. */
export const vehicleAttributeOptionsSchema = z.object({
    manufacturers: z.array(z.string().min(1)),
    segments: z.array(z.object({ id: uuidSchema, name: z.string().min(1) })),
    powertrainTypes: z.array(powertrainTypeSchema),
});
const optionalText = (max) => z.string().trim().max(max).optional();
/**
 * Form-facing versions of the shared primitives. The raw zod defaults ("Invalid uuid", "Expected
 * number, received nan") reach the user directly through `zodResolver`, so the required fields carry
 * messages a tester can act on.
 */
const requiredSegmentIdSchema = z.string().uuid('Choose a segment');
const requiredModelYearSchema = z.coerce
    .number({ invalid_type_error: 'Model year is required' })
    .int('Enter a whole year')
    .min(MIN_MODEL_YEAR, `Enter a year from ${MIN_MODEL_YEAR} onwards`)
    .max(MAX_MODEL_YEAR, `Enter a year up to ${MAX_MODEL_YEAR}`);
/**
 * A tire-pressure set is all four wheels or nothing. The web form renders four inputs that are either
 * all filled or all empty, and an API client may post the same, so an entirely blank set is normalized
 * to "absent" before validation instead of failing four `min` checks.
 */
const dropBlankTirePressureSet = (value) => {
    if (typeof value !== 'object' || value === null || !('oemTirePressureKpa' in value)) {
        return value;
    }
    const set = value.oemTirePressureKpa;
    if (typeof set !== 'object' || set === null) {
        return value;
    }
    const wheels = Object.values(set);
    const isBlank = wheels.length === 0 || wheels.every((wheel) => wheel === undefined || wheel === null || wheel === '' || Number.isNaN(wheel));
    return isBlank ? { ...value, oemTirePressureKpa: undefined } : value;
};
const createVehicleBodySchema = z.object({
    code: z
        .string()
        .trim()
        .min(1, 'Code is required')
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'Use letters, digits and hyphens only'),
    segmentId: requiredSegmentIdSchema,
    manufacturer: z.string().trim().min(1, 'Manufacturer is required').max(128),
    model: z.string().trim().min(1, 'Model is required').max(128),
    modelYear: requiredModelYearSchema,
    powertrainType: powertrainTypeSchema,
    trim: optionalText(128),
    vin: optionalText(32),
    engine: optionalText(128),
    powerHp: z.coerce.number().int().min(0).max(5000).optional(),
    torqueNm: z.coerce.number().int().min(0).max(20000).optional(),
    transmission: optionalText(128),
    drivetrain: optionalText(32),
    fuelType: optionalText(32),
    defaultFuelGrade: optionalText(32),
    tireSize: optionalText(32),
    oemTirePressureKpa: tirePressureSetSchema.optional(),
    odometerKm: z.coerce.number().int().min(0).max(9_999_999).optional(),
    notes: optionalText(2000),
});
export const createVehicleInputSchema = z.preprocess(dropBlankTirePressureSet, createVehicleBodySchema);
/** PATCH semantics: an absent key is left alone, an explicit `null` clears the column. */
const updateVehicleBodySchema = z.object({
    ...idParamSchema.shape,
    segmentId: requiredSegmentIdSchema.optional(),
    manufacturer: z.string().trim().min(1, 'Manufacturer is required').max(128).optional(),
    model: z.string().trim().min(1, 'Model is required').max(128).optional(),
    modelYear: requiredModelYearSchema.optional(),
    powertrainType: powertrainTypeSchema.optional(),
    trim: z.string().trim().max(128).nullish(),
    vin: z.string().trim().max(32).nullish(),
    engine: z.string().trim().max(128).nullish(),
    powerHp: z.coerce.number().int().min(0).max(5000).nullish(),
    torqueNm: z.coerce.number().int().min(0).max(20000).nullish(),
    transmission: z.string().trim().max(128).nullish(),
    drivetrain: z.string().trim().max(32).nullish(),
    fuelType: z.string().trim().max(32).nullish(),
    defaultFuelGrade: z.string().trim().max(32).nullish(),
    tireSize: z.string().trim().max(32).nullish(),
    oemTirePressureKpa: tirePressureSetSchema.nullish(),
    odometerKm: z.coerce.number().int().min(0).max(9_999_999).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    status: vehicleStatusSchema.optional(),
});
export const updateVehicleInputSchema = z.preprocess(dropBlankTirePressureSet, updateVehicleBodySchema);
export const createPublishedResultInputSchema = z.object({
    vehicleId: uuidSchema,
    regime: publishedRegimeSchema,
    metric: z.string().trim().min(1, 'Metric is required').max(64),
    value: z.coerce.number().min(0).max(1_000_000),
    unit: z.string().trim().min(1, 'Unit is required').max(32),
    source: optionalText(255),
    sourceDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the YYYY-MM-DD format')
        .optional(),
    market: optionalText(64),
    notes: optionalText(2000),
});
export const deletePublishedResultInputSchema = z.object({
    vehicleId: uuidSchema,
    id: uuidSchema,
});
export const deletePublishedResultOutputSchema = z.object({
    id: uuidSchema,
    deleted: z.literal(true),
});
//# sourceMappingURL=vehicles.schemas.js.map